import type { Draft, SliceCaseReducers } from '@reduxjs/toolkit';

import type {
  CompositeCommandPayload,
  SetDataViewAttributeModificationCommandPayload,
} from 'cadenza/api-client/workbook-command-payloads';
import { Command } from 'cadenza/api-client/workbook-commands';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import { CommonDataViewSettingsKey } from 'cadenza/workbook/model/common-data-view-settings-key.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key.gen';
import type { QueryExecutionStrategyType } from 'cadenza/workbook/query-execution-strategy-type';
import { getViewToDataViewConnectionsByTargetDataViewId } from 'cadenza/workbook/state/workbook-data-view-connections-selectors.state';
import {
  getActiveWorksheet,
  getDataViewById,
  getDataViewIdsByViewId,
  getViewById,
  isActiveWorksheetDirty,
  isReportMode,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

export interface OnDemandMapState {
  isOnDemandWorksheetLoading: boolean;
  /** A map containing which on demand layers from a view are being loaded. A layer is being loaded when the user clicked the "Update" button */
  onDemandLayersLoading: Record<ViewId, LayerId[]>;
  /* For preview maps, we always want to load map data, independently of the data view settings */
  shouldConsiderDataViewOnDemandSettings: boolean;
}

export function createInitialOnDemandMapState(): OnDemandMapState {
  return {
    isOnDemandWorksheetLoading: false,
    onDemandLayersLoading: {},
    shouldConsiderDataViewOnDemandSettings: true,
  };
}

export const onDemandMapReducers = {
  loadOnDemandWorksheet(state: WorkbookState) {
    state.isOnDemandWorksheetLoading = true;
  },
  loadOnDemandLayers(state: WorkbookState, { payload: viewId }: { payload: ViewId }) {
    state.onDemandLayersLoading[viewId] = getOnDemandLayersIds(state, viewId);
  },
  onDemandLayerFinishedLoading(
    state: WorkbookState,
    { payload: { layerId, viewId } }: { payload: { layerId: LayerId; viewId: ViewId } },
  ) {
    state.onDemandLayersLoading[viewId] = state.onDemandLayersLoading[viewId]?.filter((id) => id !== layerId) ?? [];
  },
  shouldConsiderDataViewOnDemandSettings(
    state: WorkbookState,
    { payload: shouldConsiderOnDemand }: { payload: boolean },
  ) {
    state.shouldConsiderDataViewOnDemandSettings = shouldConsiderOnDemand;
  },
} satisfies SliceCaseReducers<WorkbookState>;

export function updateIsOnDemandWorksheetLoading(state: Draft<WorkbookState>, command: Command) {
  if (isActiveWorksheetSettingOnDemand(state)) {
    const isOnDemandRelevantCommand = shouldOnDemandBeConsidered(state, command);
    state.isOnDemandWorksheetLoading = !isOnDemandRelevantCommand;
  }
}

function isActiveWorksheetSettingOnDemand(state: RootStateOr<WorkbookState>) {
  const activeWorksheet = getActiveWorksheet(state);
  return activeWorksheet && isWorksheetOnDemand(activeWorksheet);
}

export const isWorksheetOnDemandOverlayVisible = (state: RootStateOr<WorkbookState>) =>
  isWorksheetOnDemand(getActiveWorksheet(state)) && isActiveWorksheetDirty(state);

export const isActiveWorksheetOnDemand = (state: RootStateOr<WorkbookState>) => {
  return isActiveWorksheetSettingOnDemand(state) && !slice(state).isOnDemandWorksheetLoading && !isReportMode(state);
};

export function isWorksheetOnDemand(worksheet: WorksheetDto) {
  return (
    getSetting<QueryExecutionStrategyType>(
      worksheet,
      WorksheetSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY'),
    ) === 'onDemand'
  );
}

export const isViewOnDemand = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  const isAnyDataViewOnDemand = view.dataViews.some((dataViewId) => {
    const dataView = getDataViewById(state, dataViewId);
    return isReportMode(state) || isDataViewOnDemand(dataView);
  });
  return isAnyDataViewOnDemand && shouldOnDemandBeConsidered(state, slice(state).lastCommand, viewId);
};

export function isDataViewOnDemand(dataView: DataViewDto) {
  return (
    getSetting<QueryExecutionStrategyType>(
      dataView,
      CommonDataViewSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY'),
    ) === 'onDemand'
  );
}

export const getOnDemandLayersIds = (state: RootStateOr<WorkbookState>, viewId: ViewId): LayerId[] => {
  const view = getViewById(state, viewId);
  if (view && view.type === ViewType.MAP) {
    return Object.values(view.layerDefinitions)
      .filter(
        (layer) =>
          !isReportMode(state) &&
          getSetting(layer, 'dataViewId') &&
          isDataViewOnDemand(getDataViewById(state, getSetting(layer, 'dataViewId') as DataViewId)),
      )
      .map((layer) => layer.id);
  }
  return [];
};

export const isLayerSettingOnDemand = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) => {
  return getOnDemandLayersIds(state, viewId).includes(layerId);
};

/**
 * Should return true only if the user has clicked on the "Update" button to load the on demand layer
 *
 * @param state - The state object
 * @param viewId - The viewId in which the user clicked the button
 * @param layerId - The layer id to check whether it's being loaded
 */
export const isOnDemandLayerLoading = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) => {
  const viewOnDemandLayers = slice(state).onDemandLayersLoading[viewId] ?? [];
  return viewOnDemandLayers.includes(layerId);
};

export const isLayerOnDemandAndShouldNotLoad = (
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  layerId: LayerId,
) => {
  return (
    slice(state).shouldConsiderDataViewOnDemandSettings &&
    isLayerSettingOnDemand(state, viewId, layerId) &&
    !isOnDemandLayerLoading(state, viewId, layerId)
  );
};

export const getAutomaticallyUpdatingLayersIds = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  if (view.type === ViewType.MAP) {
    return Object.values(view.layerDefinitions)
      .filter(
        (layer) =>
          isReportMode(state) ||
          getSetting(layer, 'dataViewId') == null ||
          !isDataViewOnDemand(getDataViewById(state, getSetting(layer, 'dataViewId') as DataViewId)),
      )
      .map((layer) => layer.id);
  }
  return [];
};

export const shouldOnDemandBeConsidered = (
  state: RootStateOr<WorkbookState>,
  command: Command | undefined,
  viewId?: ViewId,
) => {
  if (isReportMode(state)) {
    return false;
  }

  if (command) {
    const isCommandEligible =
      command.type === 'COMPOSITE_COMMAND' ?
        (command.payload as CompositeCommandPayload).commands.some((c) => isCommandEligibleForOnDemand(c, state))
      : isCommandEligibleForOnDemand(command, state);
    // override ignoreInOnDemand for controlled views
    return (!command.payload.ignoreInOnDemand || isAnyDataViewInViewControlled(viewId)) && isCommandEligible;
  }
  return true;
};

function isAnyDataViewInViewControlled(viewId: ViewId | undefined) {
  return (
    !!viewId &&
    getDataViewIdsByViewId(getStore().getState(), viewId).some(
      (dataViewId) => getViewToDataViewConnectionsByTargetDataViewId(getStore().getState(), dataViewId).length,
    )
  );
}

function isCommandEligibleForOnDemand(command: Command, state: RootStateOr<WorkbookState>) {
  return (
    !isCommandOfType(command, [
      Command.SET_SELECTION.type,
      Command.INVERT_SELECTION.type,
      Command.ADD_VIEW.type,
      Command.ADD_NEW_VIEW_WITH_DATA_VIEW.type,
    ]) &&
    !isCommandOfTypeInViewType(state, command, ViewType.IMAGE, Command.CHANGE_VIEW_SETTINGS.type) &&
    !isCommandOfTypeInViewType(state, command, ViewType.TEXT, Command.CHANGE_VIEW_SETTINGS.type) &&
    !isHierarchyLevelChangeCommandDueToScaleChange(command)
  );
}

function isCommandOfType(command: Command, commandTypes: string[]) {
  return commandTypes.includes(command.type);
}

function isCommandOfTypeInViewType(
  state: RootStateOr<WorkbookState>,
  command: Command,
  viewType: ViewType,
  cmdType: string,
) {
  const viewId = (command.payload as { viewId: ViewId })?.viewId;
  if (!viewId) {
    return false;
  }
  const view = getViewById(state, viewId);
  return view && view.type === viewType && command.type === cmdType;
}

function isHierarchyLevelChangeCommandDueToScaleChange(command: Command) {
  return (
    command.type === Command.SET_DATA_VIEW_ATTRIBUTE_MODIFICATION.type &&
    (command.payload as SetDataViewAttributeModificationCommandPayload).modificationType === 'LOCAL_HIERARCHY_LEVEL'
  );
}
